<template>
    <div class="select-container select-container--portal">
        <label class="select-label" v-if="!!label">{{ label }}</label>
        <select 
            :class="`form-select ${customClass}`" 
            v-if="normal" 
            v-model="normalSelectedValue"
        >
            <option value="">{{ placeholder || 'Select an option' }}</option>
            <option v-for="option of options" :value="option.id" :key="option.id">
                {{ option.title }}
            </option>
        </select>
        <multiselect
            v-else
            :disabled="disabled"
            :multiple="multiple"
            :value="getSelectDropdownValue()"
            :options="options"
            :internal-search="internalSearch"
            :loading="loading"
            track-by="id"
            label="title"
            :placeholder="placeholder || 'Select option'"
            @input="handleChange"
            @search-change="$emit('search-change', $event)"
        ></multiselect>
    </div>
</template>

<script>
import Multiselect from "vue-multiselect"

export default {
    emits: ["change", "search-change"],
    props: {
        customClass: {
            type: String,
            default: "",
        },
        id: {
            type: String,
            default: "",
        },
        options: {
            type: Array,
            default: () => [],
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        label: {
            type: String,
            default: "",
        },
        placeholder: {
            type: String,
            default: "",
        },
        field: {
            type: String,
            default: "",
        },
        value: {
            type: [String, Object, Number, Array, Boolean],
            default: () => null,
        },
        normal: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        internalSearch: {
            type: Boolean,
            default: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        additionalDelayTrigger: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return { innerValue: null, normalSelectedValue: null }
    },
    components: { Multiselect },
    mounted() {},
    methods: {
        getSelectDropdownValue() {
            return this.multiple
                ? this.options.filter((item) => this.value && this.value.includes(item.id))
                : this.options.find((item) => item.id == this.value)
        },
        handleChange(e) {
            let emitVal
            if (e) {
                if (this.multiple) {
                    emitVal = e.map((item) => item.id)
                } else {
                    emitVal = e.id
                }
            }
            this.$emit("change", emitVal)
        },
    },
    watch: {
        normalSelectedValue(e, old) {
            if (e != old) {
                this.$emit("change", this.normalSelectedValue)
            }
        },
        value(e, old) {
            if (e != old) {
                this.normalSelectedValue = e
            }
        },
    },
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style lang="scss">
@import "@/assets/variables.scss";

/* Container styling */
.select-container {
    margin-bottom: 0;
    position: relative;
    overflow: visible;
}

/* Ensure dropdown content can overflow cards/containers */
.select-container--portal :deep(.multiselect__content-wrapper) {
    position: absolute;
    z-index: 9999;
    max-height: 320px;
}

/* Label styling */
.select-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: $textColor;
    margin-bottom: 6px;
}

/* Regular select styling */
.form-select {
    display: block;
    width: 100%;
    padding: 8px 12px;
    font-size: 0.875rem;
    line-height: 1.5;
    color: $textColor;
    background-color: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 4px;
    appearance: none;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 0.75rem center;
    background-size: 16px 12px;
    
    &:focus {
        border-color: $primaryColor;
        outline: 0;
        box-shadow: 0 0 0 2px rgba($primaryColor, 0.2);
    }
}

/* Multiselect styling */
.multiselect {
    min-height: 38px;
    border-radius: 4px;
}

.multiselect__tags {
    min-height: 38px;
    padding: 6px 40px 0 8px;
    border: 1px solid $lightBorderColor;
    border-radius: 4px;
    font-size: 0.875rem;
    white-space: normal;           /* allow wrapping */
    line-height: 1.25;
}

.multiselect__tag {
    background: $primaryColor;
    margin-right: 5px;
    margin-bottom: 3px;
    padding: 4px 26px 4px 10px;
    border-radius: 4px;
}

.multiselect__tag-icon {
    line-height: 22px;
}

.multiselect__tag-icon:after {
    color: white;
}

.multiselect__tag-icon:hover {
    background: darken($primaryColor, 10%);
}

.multiselect__input,
.multiselect__single {
    font-size: 0.875rem;
    padding-left: 0;
    margin-bottom: 5px;
}

.multiselect__select {
    height: 38px;
}

.multiselect__content-wrapper {
    border-color: $lightBorderColor;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    max-height: 360px;
}

.multiselect__option {
    font-size: 0.875rem;
    padding: 9px 12px;
    min-height: 36px;
    line-height: 1.2;
    white-space: normal;           /* allow long text to wrap */
}

/* Fix for the "Press enter to select" overlay */
.multiselect__option:after {
    content: none !important;
}

.multiselect__option--selected:after {
    content: none !important;
}

.multiselect__option--highlight:after {
    content: none !important;
}

.multiselect__option--highlight {
    background: $primaryColor;
}

.multiselect__option--selected.multiselect__option--highlight {
    background: $accentColor;
}

/* Dark theme styling */
.dark {
    .select-label {
        color: $darkTextColor;
    }
    
    .form-select {
        background-color: $darkInputBg;
        border-color: $darkBorderColor;
        color: $darkTextColor;
        
        &:focus {
            border-color: $secondaryColor;
            box-shadow: 0 0 0 2px rgba($secondaryColor, 0.2);
        }
    }
    
    .multiselect__tags {
        background-color: $darkInputBg;
        border-color: $darkBorderColor;
    }
    
    .multiselect__input,
    .multiselect__single {
        background-color: $darkInputBg;
        color: $darkTextColor;
    }
    
    .multiselect__content-wrapper {
        background-color: $darkCardBg;
        border-color: $darkBorderColor;
    }
    
    .multiselect__option {
        color: $darkTextColor;
        
        &.multiselect__option--highlight {
            background-color: $primaryColor;
            color: white;
        }
        
        &.multiselect__option--selected.multiselect__option--highlight {
            background-color: $accentColor;
            color: white;
        }
    }
}
</style>
